import React, { useEffect, useState, useRef, useContext } from 'react'
import { RouteComponentProps } from 'react-router-dom'
import axios from 'axios'
import moment from 'moment-timezone'
import { connect } from 'react-redux'
import { Dispatch } from 'redux'

import {
  Grid,
  Box,
  Paper,
  Typography,
  Checkbox,
  TextField,
  Button,
  FormControl,
  FormGroup,
  FormControlLabel
} from '@material-ui/core'
import { makeStyles, Theme } from '@material-ui/core/styles'

import Title from '../components/atoms/Title'
import SelectInput from '../components/atoms/SelectInput'
import CenteredSubmitButton from '../components/molecules/CenteredSubmitButton'
import DateTimePicker from '../components/molecules/DateTimePicker'
import DatePicker from '../components/molecules/DatePicker'
import TimePicker from '../components/molecules/TimePicker'
import DefaultTab from '../components/organisms/DefaultTab'
import TinyMCE from '../components/organisms/TinyMCE'
import InputExternalLink from '../components/organisms/InputExternalLink'
import InputFileLink from '../components/organisms/InputFileLink'
import { Event } from '../types/Event'
import { Admin } from '../types/Admin'
import { formUploadHelper } from '../helpers'
import store from '../store'
import { setAuth } from '../store/actions/auth'
import ConfigContext from '../ConfigContext'

const validUrl = require('valid-url')

const useStyles = makeStyles((theme: Theme) => ({
  paper: {
    padding: theme.spacing(2),
    display: 'flex',
    overflow: 'auto',
    flexDirection: 'column',
    height: `100%`
  },
  textField: {
    paddingRight: theme.spacing(1)
  }
}))

const EventCMS: React.FC<
  RouteComponentProps<{ event_id: string }> &
    ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>
> = ({ match, history, admin, setAuth }) => {
  const classes = useStyles({})
  const eventId = match.params.event_id
  const currentPath = match.path
  const [event, setEvent] = useState<{
    title: string
    content: string
    external_link: string
    file_link: string
    release_date: Date | null
    close_date: Date | null
    start_date: Date | null
    end_date: Date | null
    all_day_flg: boolean
    close_flg: boolean
    sender: string
  }>({
    title: '',
    content: '',
    external_link: '',
    file_link: '',
    release_date: moment({ hour: 0 }).toDate(),
    close_date: null,
    start_date: moment({ hour: 0 }).toDate(),
    end_date: moment({ hour: 0 }).toDate(),
    all_day_flg: false,
    close_flg: false,
    sender: ''
  })
  const [fileObject, setFileObject] = useState<File | null>(null)
  // s3対応
  // const [fileObject, setFileObject] = useState<{ file: File | null; filename: string }>({
  //   file: null,
  //   filename: ''
  // })
  const [fileName, setFileName] = useState<string>('')
  const [admins, setAdmins] = useState<{ value: string; label: string }[]>([])
  const [type, setType] = useState<'create' | 'edit'>('create')
  const [initTab, setInitTab] = useState<number>(1)
  const [deleteFileLink, setDeleteFileLink] = useState<string>('')
  const editorRef = useRef(null)
  const config = useContext(ConfigContext)

  const tabs = [
    {
      value: 1,
      label: '文字情報',
      component: (
        <TinyMCE
          initialValue={event.content}
          editorRef={editorRef}
          inputHandler={(tinyMCEContent: string) => {
            setDeleteFileLink(event.file_link)
            setEvent({
              ...event,
              content: tinyMCEContent,
              external_link: '',
              file_link: ''
            })
            setFileObject(null)
            // s3対応
            // setFileObject({
            //   file: null,
            //   filename: ''
            // })
          }}
        />
      )
    },
    {
      value: 2,
      label: '外部リンク設定',
      component: (
        <InputExternalLink
          value={event.external_link}
          checked={null}
          inputHandler={(input: string) => {
            setDeleteFileLink(event.file_link)
            setEvent({
              ...event,
              content: '',
              external_link: input,
              file_link: ''
            })
            editorRef.current.editor.setContent('')
            setFileObject(null)
            // s3対応
            // setFileObject({
            //   file: null,
            //   filename: ''
            // })
          }}
          checkHandler={null}
        />
      )
    },
    {
      value: 3,
      label: 'ファイルリンク設定',
      component: (
        <InputFileLink
          fileObject={fileObject}
          inputHandler={(input: File) => {
            if (event.file_link) {
              setDeleteFileLink(event.file_link)
            }
            setEvent({
              ...event,
              content: '',
              external_link: ''
            })
            editorRef.current.editor.setContent('')
            setFileObject(input)
            // s3対応
            // setFileObject({
            //   file: input,
            //   filename: ''
            // })
          }}
          deleteHandler={() => {
            setDeleteFileLink(event.file_link)
            setFileObject(null)
            // s3対応
            // setFileObject({
            //   file: null,
            //   filename: ''
            // })
            setEvent({
              ...event,
              file_link: ''
            })
          }}
          changeFilenameHandler={filename => setFileName(filename)}
        />
      )
    }
  ]

  /**
   * uriからFileオブジェクトを作成する関数
   * @param uri
   * @param fileName
   */
  const getFileObjectFromURI = async (uri: string, fileName: string) => {
    const { data } = await axios.get(uri, {
      responseType: 'arraybuffer'
    })
    return new File([data as ArrayBuffer], fileName)
  }

  /**
   * 初期化処理
   */
  useEffect(() => {
    const fetchEvent = async () => {
      const { data } = await axios.get<Event>(`/events/${eventId}`)
      if (data.file_link) {
        const originalFileName = data.file_link.replace(/\/static\/upload\/.*\//, '')
        setFileObject(await getFileObjectFromURI(data.file_link, originalFileName))
        // s3対応
        // setFileObject({
        //   file: null,
        //   filename: `${config.serverHost}/upload/image/?key=${encodeURIComponent(data.file_link)}`
        // })
      }
      setEvent({
        title: data.title,
        content: data.content,
        external_link: data.external_link,
        file_link: data.file_link,
        release_date: moment(data.release_date).toDate(),
        close_date: data.close_date ? moment(data.close_date).toDate() : null,
        start_date: moment(data.start_date).toDate(),
        end_date: moment(data.end_date).toDate(),
        all_day_flg: Boolean(data.all_day_flg),
        close_flg: Boolean(data.close_flg),
        sender: data.sender
      })
      if (data.content) {
        setInitTab(1)
      } else if (data.external_link) {
        setInitTab(2)
      } else if (data.file_link) {
        setInitTab(3)
      }
    }

    const fetchAdmins = async () => {
      const { data } = await axios.get<Admin[]>(`/admins`)
      setAdmins(
        data.map(admin => ({
          value: admin.name,
          label: admin.name
        }))
      )
      const loginAdmin = data.filter(d => d.admin_id === admin.adminId)
      setAuth(admin.id, admin.token, admin.adminId, admin.password, loginAdmin[0].name)
    }
    fetchAdmins()

    // 現在のパスから新規作成処理なのか、更新処理なのかを判断する
    if (currentPath === '/create-event') {
      setType('create')
      event.sender = admin.name
    } else {
      setType('edit')
      fetchEvent()
    }
  }, [currentPath, eventId])

  /**
   * 登録
   */
  const handleSubmit = async (submitEvent: React.FormEvent<HTMLFormElement>) => {
    try {
      submitEvent.preventDefault()
      if (moment(event.release_date) > moment(event.close_date)) {
        alert('掲載開始日時を、掲載終了日時より後に設定することはできません。')
        return
      }
      if (moment(event.start_date) > moment(event.end_date)) {
        alert('開催開始日時を、開催終了日時より後に設定することはできません。')
        return
      }
      if (moment(event.start_date).format('HH:mm') > moment(event.end_date).format('HH:mm')) {
        alert('開催開始時間を、開催終了時間より後に設定することはできません。')
        return
      }
      if (event.external_link && !validUrl.isWebUri(event.external_link)) {
        alert('不正なリンクURLです。変更してください。')
        return
      }

      let fileLink: string = ''
      if (fileObject instanceof File) {
        fileLink = await formUploadHelper(fileName, fileObject, '/upload/event', true)
      }
      // s3対応
      // if (fileObject.file instanceof File) {
      //   fileLink = await formUploadHelper(fileName, fileObject.file, '/upload/event', true)
      // }
      if (deleteFileLink) {
        await axios.delete('/upload', {
          data: { path: deleteFileLink }
        })
      }

      const requestBody = {
        ...event,
        release_date: event.release_date
          ? moment(event.release_date).format('YYYY-MM-DD HH:mm')
          : null,
        close_date: event.close_date ? moment(event.close_date).format('YYYY-MM-DD HH:mm') : null,
        start_date: event.start_date ? moment(event.start_date).format('YYYY-MM-DD HH:mm') : null,
        end_date: event.end_date ? moment(event.end_date).format('YYYY-MM-DD HH:mm') : null,
        file_link: fileLink ? fileLink : event.file_link
      }
      if (type === 'create') {
        // イベントを新規作成の場合
        await axios.post(`/events`, requestBody)
      } else {
        // イベント更新の場合
        await axios.put(`/events/${eventId}`, requestBody)
      }
      history.push('/events')
    } catch (error) {
      alert(error.message)
    }
  }

  return (
    <React.Fragment>
      <Grid item xs={12}>
        <Paper className={classes.paper}>
          <Box display="flex" justifyContent="space-between">
            <Title>イベント新規作成／編集</Title>
            <Box ml={3}>
              <Button color="primary" variant="outlined" href="/events">
                一覧に戻る
              </Button>
            </Box>
          </Box>
          <form onSubmit={handleSubmit}>
            <Box>
              <Typography variant="subtitle2" color="error">
                ※は必須項目です。
              </Typography>
            </Box>
            <Box mb={5}>
              <Grid container alignItems="center" spacing={2}>
                <Grid item xs={12} sm={2} md={1}>
                  <Typography variant="subtitle2">
                    イベント名
                    <Typography variant="caption" color="error" style={{ marginLeft: '3px' }}>
                      ※
                    </Typography>
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={10} md={5}>
                  <Box width={400}>
                    <TextField
                      value={event.title}
                      variant="outlined"
                      placeholder="イベント名を入力"
                      onChange={(_event: React.ChangeEvent<HTMLInputElement>) =>
                        setEvent({ ...event, title: _event.target.value })
                      }
                      fullWidth
                      required
                    />
                  </Box>
                </Grid>

                <Grid item xs={12} sm={2} md={1}>
                  <Typography variant="subtitle2">
                    発信者
                    <Typography variant="caption" color="error" style={{ marginLeft: '3px' }}>
                      ※
                    </Typography>
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={10} md={5}>
                  <SelectInput
                    value={event.sender}
                    selectHandler={(select: string) => {
                      admins.forEach((admin, index) => {
                        if (select === admin.value) {
                          setEvent({ ...event, sender: admin.value })
                        }
                      })
                    }}
                    options={admins}
                    width={400}
                    required
                  />
                </Grid>

                <Grid item xs={12} sm={2} md={1}>
                  <Typography variant="subtitle2">
                    掲載開始日時
                    <Typography variant="caption" color="error" style={{ marginLeft: '3px' }}>
                      ※
                    </Typography>
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={10} md={5}>
                  <Box width={400}>
                    <DateTimePicker
                      value={event.release_date}
                      pickerHandler={(date: Date | null) =>
                        setEvent({ ...event, release_date: date })
                      }
                      required
                    />
                  </Box>
                </Grid>

                <Grid item xs={12} sm={2} md={1}>
                  <Typography variant="subtitle2">掲載終了日時</Typography>
                </Grid>
                <Grid item xs={12} sm={10} md={5}>
                  <Box width={400}>
                    <DateTimePicker
                      label="日時を選択"
                      value={event.close_date}
                      pickerHandler={(date: Date | null) =>
                        setEvent({ ...event, close_date: date })
                      }
                    />
                  </Box>
                </Grid>

                <Grid item xs={12} sm={2} md={1}>
                  <Typography variant="subtitle2">
                    開催期間
                    <Typography variant="caption" color="error" style={{ marginLeft: '3px' }}>
                      ※
                    </Typography>
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={10} md={5}>
                  <Box display="flex" alignItems="center">
                    <Box width={240} display="flex" alignItems="center" mr={1}>
                      <DatePicker
                        required
                        value={event.start_date}
                        pickerHandler={(date: Date | null) => {
                          if (date) {
                            let startDate: string = moment(date)
                              .hour(moment(event.start_date).hour())
                              .minute(moment(event.start_date).minute())
                              .format('YYYY-MM-DD HH:mm')
                            setEvent({ ...event, start_date: new Date(startDate) })
                          } else {
                            setEvent({ ...event, start_date: date })
                          }
                        }}
                      />
                    </Box>
                    <Typography variant="body1">{'～'}</Typography>
                    <Box width={240} display="flex" alignItems="center" ml={1}>
                      <DatePicker
                        required
                        value={event.end_date}
                        pickerHandler={(date: Date | null) => {
                          if (date) {
                            let endDate: string = moment(date)
                              .hour(moment(event.end_date).hour())
                              .minute(moment(event.end_date).minute())
                              .format('YYYY-MM-DD HH:mm')
                            setEvent({ ...event, end_date: new Date(endDate) })
                          } else {
                            setEvent({ ...event, end_date: date })
                          }
                        }}
                      />
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={2} md={1}>
                  <Typography variant="subtitle2">
                    開催時間
                    <Typography variant="caption" color="error" style={{ marginLeft: '3px' }}>
                      ※
                    </Typography>
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={10} md={5}>
                  <Box display="flex">
                    <Box display="flex" alignItems="center" mr={2}>
                      <FormControl component="fieldset" style={{ marginLeft: '1rem' }}>
                        <FormGroup aria-label="position" row>
                          <FormControlLabel
                            value="top"
                            control={
                              <Checkbox
                                checked={event.all_day_flg}
                                onChange={(
                                  changeEvent: React.ChangeEvent<HTMLInputElement>,
                                  checked: boolean
                                ) => {
                                  // 終日フラグがオンになると、時間、分、秒データを0にする
                                  setEvent({
                                    ...event,
                                    all_day_flg: checked,
                                    start_date:
                                      event.start_date instanceof Date
                                        ? moment(event.start_date)
                                            .set({
                                              hour: 0,
                                              minute: 0,
                                              second: 0,
                                              millisecond: 0
                                            })
                                            .toDate()
                                        : null,
                                    end_date:
                                      event.end_date instanceof Date
                                        ? moment(event.end_date)
                                            .set({
                                              hour: 0,
                                              minute: 0,
                                              second: 0,
                                              millisecond: 0
                                            })
                                            .toDate()
                                        : null
                                  })
                                }}
                              />
                            }
                            label="終日"
                            labelPlacement="end"
                          />
                        </FormGroup>
                      </FormControl>
                    </Box>
                    {!event.all_day_flg && (
                      <Box display="flex" alignItems="center">
                        <Box width={150} display="flex" alignItems="center" mr={1}>
                          <TimePicker
                            pickerHandler={(date: Date | null) => {
                              if (event.start_date) {
                                let startDate: string = moment(event.start_date)
                                  .hour(date === null ? 0 : moment(date).hour())
                                  .minute(date === null ? 0 : moment(date).minute())
                                  .format('YYYY-MM-DD HH:mm')
                                setEvent({
                                  ...event,
                                  start_date: new Date(startDate)
                                })
                              }
                            }}
                            value={event.start_date}
                            required
                          />
                        </Box>
                        <Typography variant="body1">{'～'}</Typography>
                        <Box width={150} display="flex" alignItems="center" ml={1}>
                          <TimePicker
                            pickerHandler={(date: Date | null) => {
                              if (event.end_date) {
                                let endDate: string = moment(event.end_date)
                                  .hour(date === null ? 0 : moment(date).hour())
                                  .minute(date === null ? 0 : moment(date).minute())
                                  .format('YYYY-MM-DD HH:mm')
                                setEvent({
                                  ...event,
                                  end_date: new Date(endDate)
                                })
                              }
                            }}
                            value={event.end_date}
                            required
                          />
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12} sm={2} md={1}>
                  <Typography variant="subtitle2">非公開</Typography>
                </Grid>
                <Grid item xs={12} sm={10} md={6}>
                  <Box display="flex" alignItems="center" mr={5}>
                    <FormControl component="fieldset" style={{ marginLeft: '1rem' }}>
                      <FormGroup aria-label="position" row>
                        <FormControlLabel
                          value="top"
                          control={
                            <Checkbox
                              checked={event.close_flg}
                              onChange={(
                                changeEvent: React.ChangeEvent<HTMLInputElement>,
                                checked: boolean
                              ) => setEvent({ ...event, close_flg: checked })}
                            />
                          }
                          label="非公開にする"
                          labelPlacement="end"
                        />
                      </FormGroup>
                    </FormControl>
                    <Typography variant="caption">
                      ※掲載開始日時になっても表示されません。
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={10} md={5}>
                  <Box>
                    <Typography variant="caption" component="div">
                      ※「終日」にチェックを付けるか、もしくは開始と終了時間を設定してください。
                    </Typography>
                    <Typography variant="caption" component="div">
                      ※期間イベントの場合は、全日程共通の時間となります。
                    </Typography>
                    <Typography variant="caption" component="div" style={{ paddingLeft: '1em' }}>
                      期間日程毎に時間が異なる場合は「文字情報」欄を活用ください。
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            <Box my={3} marginTop={18}>
              <Box>
                <DefaultTab tabs={tabs} initTab={initTab} />
              </Box>
            </Box>
            <Box mb={10}>
              <CenteredSubmitButton
                submittable
                resettable
                resetText="リセット"
                submitHandler={() => {}}
                resetHandler={() => {
                  window.location.reload()
                }}
                size="large"
              />
            </Box>
          </form>
        </Paper>
      </Grid>
    </React.Fragment>
  )
}
const mapDispatchToProps = (dispatch: Dispatch) => {
  return {
    setAuth: (id: number, token: string, adminId: string, password: string, name: string) =>
      dispatch(setAuth(id, token, adminId, password, name))
  }
}
const mapStateToProps = (state: ReturnType<typeof store.getState>) => {
  return {
    admin: state.auth
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(EventCMS)
